/**
 * appVersion.js
 *
 * The running build's version, and the comparison behind the update gate.
 *
 * A native build is frozen at the moment it was signed: a fix pushed to the web
 * reaches every browser on the next load, but an iPhone keeps running whatever
 * TestFlight or the App Store last installed until its owner updates. The gate
 * is how a build that must not keep running can be told to stop.
 *
 * The version is baked in at build time from package.json, which is also what
 * the fastlane lane writes into MARKETING_VERSION, so the string here is the
 * one App Store Connect shows. It cannot be read at runtime: package.json sits
 * outside src/ and CRA's module scope plugin refuses the import.
 *
 * With no version baked in, the gate is off. That is deliberate: a missing
 * environment variable must not lock anybody out of the app.
 */

/** The build's marketing version, or null when it was not baked in. */
export function getAppVersion() {
  return process.env.REACT_APP_VERSION || null;
}

// Splits a dotted version into numbers. Anything that is not a number counts
// as zero, so a prerelease suffix degrades rather than throwing.
const parts = (version) =>
  String(version)
    .trim()
    .split(".")
    .map((p) => {
      const n = parseInt(p, 10);
      return Number.isNaN(n) ? 0 : n;
    });

const isVersion = (v) => typeof v === "string" && /^\d+(\.\d+)*$/.test(v.trim());

/**
 * Compares two dotted versions. Returns a negative number when a is older than
 * b, 0 when they are equal, positive when a is newer.
 *
 * Segment counts do not have to match: the shorter side is padded with zeros,
 * so "1.2" and "1.2.0" are the same version.
 */
export function compareVersions(a, b) {
  const left = parts(a);
  const right = parts(b);
  const length = Math.max(left.length, right.length);
  for (let i = 0; i < length; i += 1) {
    const diff = (left[i] || 0) - (right[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

/**
 * Whether this build is below the floor and must be blocked.
 *
 * Fails open at every step. An absent version, an absent floor, or a string
 * that is not a version at all all return false, because the cost of a wrong
 * true is an unusable app on a device the user cannot fix from inside the app.
 *
 * @param {{version: string|null, minVersion: string|null}} args
 */
export function isUpdateRequired({ version, minVersion }) {
  if (!isVersion(version) || !isVersion(minVersion)) return false;
  return compareVersions(version, minVersion) < 0;
}
